<script setup lang="ts">
import type { StyleValue } from "vue"

/**
 * Until the audio file is cached, we display a default "non-smart" audio visualizer.
 * Once the file is downloaded, we use the sandbox to generate the audio waveform and
 * render it for the user.
 *
 * TODO: use masking / bg element to speed up visuals
 * TODO: add controls
 */

const props = defineProps({
  file: null,
  containerWidth: null,
  showName: { type: Boolean },
  autoplay: { type: Boolean },
  loading: { type: Boolean },
  matchedChunks: null
})
const { file } = toRefs(props)
const { rendererState } = useRenderer({ file })
const { sandboxWorker } = useSandbox()
const audioElement = useTemplateRef("audioElement")
const waveformContainer = useTemplateRef("waveformContainer")
const streamingUrl = computed(() => rendererState.value.objectUrl || rendererState.value.presignedUrl)

const loaded = ref(false)
const muted = ref(false)
const playing = ref(props.autoplay) // autoplay
const volume = ref(1)
// const peaks = ref(Array.from({ length: numPeaks.value }, () => 0.25))
const durationSeconds = ref<number>()
const currentSeconds = ref<number>()
const bufferedSeconds = ref<number>()

const peaks = ref(Array.from({ length: Math.ceil(props.containerWidth / 2) }, () => 0.25))
let calculatingPeaks = false

watchImmediate(
  () => ({ width: props.containerWidth, path: rendererState.value.sandboxPath }),
  async ({ width, path }) => {
    if (calculatingPeaks)
      return
    const numPeaks = Math.ceil(width / 2)
    const initial = Array.from({ length: numPeaks }, () => 0.25)
    if (!path || !width) {
      peaks.value = initial
      return
    }

    try {
      calculatingPeaks = true
      const waveform: WaveformData = await sandboxWorker().extractCachedAudioWaveformSync(path, numPeaks)
      peaks.value = waveform.peaks
    }
    catch (error) {
      console.error("Error fetching cached audio waveform:", error)
    }
    finally {
      calculatingPeaks = false
    }
  },
)

/**
 * Styles for rendering each chunk
 */
const chunkStyles = computed(() => {
  if (!props.matchedChunks || props.matchedChunks.length === 0)
    return undefined
  const durationMs = props.file.mediaDurationMs()
  if (!durationMs)
    return undefined

  const segments: StyleValue[] = []
  const chunks = props
    .matchedChunks
    .filter(chunk => chunk.chunkType === "polyEmbedV1Page")
    .filter(chunk => isDefined(chunk.startOffsetSec) && isDefined(chunk.endOffsetSec))
    .sort((a, b) => a.startOffsetSec! - b.startOffsetSec!)
  const maxScore = Math.max(...chunks.map(chunk => chunk.score))
  const durationSec = durationMs / 1000

  for (const chunk of chunks) {
    if (chunk.chunkType !== "polyEmbedV1Page")
      continue
    if (!isDefined(chunk.startOffsetSec) || !isDefined(chunk.endOffsetSec))
      continue
    const start = ((chunk.startOffsetSec / durationSec) * 100)
    const end = ((chunk.endOffsetSec / durationSec) * 100)

    // Visually, opacity doesn't scale linearly, so in order to make the distinction "sharper",
    // we raise the score to a power.
    segments.push({
      left: `${start}%`,
      width: `${end - start}%`,
      opacity: (chunk.score / maxScore) ** 10,
    })
  }
  return segments
})

const durationFraction = computed(() => {
  if (!durationSeconds.value || durationSeconds.value === 0)
    return 0
  if (!currentSeconds.value)
    return 0
  return currentSeconds.value / durationSeconds.value
})

const update = () => {
  if (!audioElement.value)
    return
  durationSeconds.value = audioElement.value.duration
  currentSeconds.value = audioElement.value.currentTime
  bufferedSeconds.value = audioElement.value.buffered.length > 0 ? audioElement.value.buffered.end(0) : 0
  if (audioElement.value.readyState >= 2)
    loaded.value = true
}

const seekTo = (percent: number) => {
  if (!audioElement.value)
    return
  currentSeconds.value = audioElement.value.duration * percent
  audioElement.value.currentTime = audioElement.value.duration * percent
}
const _stop = () => {
  if (!audioElement.value)
    return
  playing.value = false
  audioElement.value.pause()
  audioElement.value.currentTime = 0
}
const pause = () => {
  if (!audioElement.value)
    return
  playing.value = false
  audioElement.value.pause()
}
const play = () => {
  if (!audioElement.value)
    return
  playing.value = true
  audioElement.value.play()
}

const toggle = () => {
  if (playing.value)
    pause()
  else play()
}

const setVolume = (newVolume: number) => {
  if (!audioElement.value)
    return
  volume.value = newVolume
  audioElement.value.volume = newVolume
}

const _mute = () => {
  if (!audioElement.value)
    return
  audioElement.value.muted = true
}

const _unmute = () => {
  if (!audioElement.value)
    return
  audioElement.value.muted = false
  if (volume.value === 0)
    setVolume(0.5)
}

const _toggleMute = () => {
  if (muted.value)
    _unmute()
  else
    _mute()
}

const convertTimeHHMMSS = (seconds: number | undefined) => {
  if (seconds === undefined || Number.isNaN(seconds))
    return "00:00"
  const hhmmss = new Date(seconds * 1000).toISOString().substr(11, 8)
  return hhmmss.indexOf("00:") === 0 ? hhmmss.substr(3) : hhmmss
}

const clickedWaveform = (event: MouseEvent) => {
  if (!audioElement.value || !waveformContainer.value)
    return
  const rect = waveformContainer.value.getBoundingClientRect()
  const percent = (event.clientX - rect.left) / rect.width
  seekTo(percent)
  play()
}
</script>

<template>
  <div class="audio-player">
    <div class="controls">
      <div
        class="play-pause"
        :class="{ loading }"
        @click="toggle"
      >
        <icon-action
          v-if="playing"
          type="pause"
          size="xs"
        />
        <icon-action
          v-else
          type="play"
          size="xs"
        />
      </div>
      <div v-if="showName" class="file-name">
        {{ file.name() }}
      </div>
    </div>
    <div v-if="chunkStyles" class="chunks">
      <div
        v-for="style, key in chunkStyles"
        :key
        class="chunk"
        :style
      />
    </div>
    <div
      v-if="len(peaks)"
      ref="waveformContainer"
      class="waveform"
      @click="clickedWaveform"
    >
      <template v-for="(slice, index) in peaks" :key="index">
        <div
          class="peak"
          :style="{
            height: `${Math.max(3, slice * 100)}%`,
            opacity: durationFraction <= (index / len(peaks)) ? 0.3 : 1,
          }"
        />
        <div v-if="index < len(peaks) - 1" class="between" />
      </template>
    </div>
    <div class="timestamps">
      <div class="current">
        {{ convertTimeHHMMSS(currentSeconds) }}
      </div>
      <div class="total">
        {{ convertTimeHHMMSS(durationSeconds) }}
      </div>
    </div>
    <div
      v-show="durationFraction > 0"
      class="cursor-container"
      :style="{ left: `${durationFraction * 100}%` }"
    >
      <div class="cursor" />
    </div>
    <audio
      v-if="streamingUrl"
      ref="audioElement"
      controls
      :src="streamingUrl"
      class="hidden-audio"
      :autoplay
      @timeupdate="update"
      @loadeddata="update"
      @play="playing = true"
      @pause="playing = false"
      @ended="update"
    />
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

audio
  // Our font features seem to affect the inner element in chrome so we reverse
  // it for the element
  font-feature-settings: normal

.audio-player
  // TODO: boltzmann
  @apply bg-[#07060866] h-full w-full outline outline-0.75 outline-[#55575F40]
  @apply relative
  @apply flex flex-col
  @apply rounded-[6px]
  @apply py-2
.controls
  @apply flex items-center gap-1 h-[18px]
  @apply px-2
  .play-pause
    @apply -ml-1 // icons are a bit narrow
    &.loading
      @apply opacity-25
    &:not(.loading)
      @apply cursor-pointer
    @apply w-4 h-4
    &:hover
      @apply text-white
  .file-name
    @apply t-menu-sm
.waveform
  @apply w-full h-full flex items-center z-0
  @apply cursor-pointer
  .peak
    @apply w-full bg-[#A6A6A6]
    transition: height 0.3s ease-in-out, opacity 0.1s linear
  .between
    @apply w-full h-0

.chunks
  @apply absolute top-0 left-0 w-full h-full pointer-events-none
.chunk
  @apply absolute bg-$global-blue-primary h-full

.timestamps
  @apply t-property-sm flex justify-between items-center
  @apply px-2

.cursor-container
  @apply absolute top-0 h-full left-0 w-2 -mx-1 z-1 flex flex-col items-center
  .cursor
    @apply w-0.33 h-full bg-$global-amber-primary
  // TODO: grabbing the audio selection
  // &:hover
  //   .cursor
  //     @apply w-0.75

.hidden-audio
  @apply hidden absolute
</style>
